import Contrat from '@/generated/classes/Contrat';
import { type ControleSignatureMasseManque, AppliquerObjetCommunQuery } from '@/extend/classes/ResultatContratMasse';
import { ControleSignatureMasseManqueCodes } from '@/generated/classes/ResultatContratMasse';
import { type EtapeControle, type Manque, type SourceControle } from '@/custom/helpers/signatureMasse';

// Étape « objet du contrat » de l'envoi en signature en masse (OVA-4980).
//
// Le serveur repère les contrats sans objet (`controleSignatureMasse`, manque OBJET_MANQUANT) et applique
// l'objet commun (`appliquerObjetCommun`) en ne touchant que les objets encore vides : un objet déjà
// renseigné n'est jamais écrasé, même s'il l'a été entre le contrôle et l'enregistrement. Le client ne
// fait que demander l'objet et transmettre.
//
// La saisie est déléguée à l'appelant (`SaisieObjet`) : l'étape reste sans interface, comme l'étape
// société délègue l'ouverture de ses modales à `ouvrirEtAttendre`.

export const MANQUE_OBJET = ControleSignatureMasseManqueCodes.OBJET_MANQUANT;

/** Pilotage de la modale de saisie, fourni par la liste des contrats. */
export interface SaisieObjet {
  /**
   * Ouvre la modale. Résout l'objet saisi, ou `null` si l'utilisateur annule. Après validation, la
   * modale reste ouverte en état « en cours » jusqu'à `terminer` : l'enregistrement a lieu entre-temps.
   */
  demander(selectionnes: number, sansObjet: number): Promise<string | null>;
  /** Ferme la modale. */
  terminer(): void;
}

/** Ce que l'étape a fait, pour le message de fin. */
export interface BilanObjet {
  /** Contrats auxquels l'objet a été appliqué. */
  appliques: number;
  /** Contrats dont l'enregistrement a échoué (le contrat reste sans objet, donc exclu). */
  echecs: number;
  /** L'appel d'enregistrement lui-même a échoué (réseau, refus serveur) : rien n'a été appliqué. */
  erreurTechnique: boolean;
}

export type EtapeObjetCommun = EtapeControle & { bilan(): BilanObjet };

/**
 * Crée l'étape objet. `selectionnes` est le nombre de contrats de la sélection, affiché par la modale
 * (X de l'US) ; les contrats sans objet (Y) viennent du contrôle.
 */
export function creerEtapeObjetCommun(selectionnes: number, saisie: SaisieObjet,
  source: SourceControle): EtapeObjetCommun {
  let manquesServeur: ControleSignatureMasseManque[] = [];
  const bilan: BilanObjet = { appliques: 0, echecs: 0, erreurTechnique: false };

  return {
    code: 'CONTRAT',

    async charger(contrats: Contrat[]): Promise<void> {
      manquesServeur = await source.manques(contrats);
    },

    controler(): Manque[] {
      return manquesServeur
        .filter((manque) => manque.code == MANQUE_OBJET)
        .map((manque) => ({ code: manque.code, libelle: manque.libelle, contratIds: manque.contratIds }));
    },

    async completer(_contrats: Contrat[], manques: Manque[]): Promise<boolean> {
      const contratIds = manques.flatMap((manque) => manque.contratIds);
      if (contratIds.length == 0) return false;

      try {
        const objet = await saisie.demander(selectionnes, contratIds.length);
        if (objet == null) {
          // Annulation : rien n'est écrit, les contrats sans objet seront exclus.
          return false;
        }
        const query = new AppliquerObjetCommunQuery();
        query.contratIds = contratIds;
        query.objet = objet;
        try {
          const resultat = await Contrat.appliquerObjetCommun(query);
          bilan.appliques = resultat.appliques;
          bilan.echecs = resultat.echecs;
        } catch {
          // Pas d'interruption du parcours : le contrôle rejoué constatera que l'objet manque toujours,
          // et les contrats concernés seront exclus. Le message de fin dira pourquoi.
          bilan.erreurTechnique = true;
        }
        return true;
      } finally {
        saisie.terminer();
      }
    },

    bilan(): BilanObjet {
      return bilan;
    },
  };
}
